import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../AuditLog/AuditLog.css";            // shares the Log chrome
import "./ExpiryLog.css";                     // adds page-specific tone
import api from "../../services/api";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { classifyType, EXPIRY_EVENT_TYPES } from "../AuditLog/eventTypes";

/*
 * ExpiryLog — dedicated view for expiry-related audit events.
 *
 * The main Log page hides `TAG_ENDDATE_SET`, `TAG_ENDDATE_CLEARED`,
 * `TAG_NEARING_END`, and `TAG_EXPIRED`; this page is the only place they
 * show up. Built on the existing /audit-events endpoint (with `type=` set
 * to the expiry whitelist), so it inherits the same filtering, sorting,
 * and pagination the generic Log has — just scoped.
 *
 * Adds a small summary strip across the top with a count for each event
 * family so an admin can tell at a glance whether anything urgent happened.
 */

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// Human-friendly time formatter — same logic as the generic Log page.
const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "ago" : "from now";
  if (abs < 45 * 1000)        return "just now";
  if (abs < 60 * 60 * 1000)   return `${Math.round(abs / 60000)} min ${sign}`;
  if (abs < 24 * 60 * 60 * 1000) return `${Math.round(abs / 3600000)} h ${sign}`;
  if (abs < 7 * 24 * 60 * 60 * 1000) return `${Math.round(abs / 86400000)} d ${sign}`;
  return d.toLocaleString();
};
const formatExact = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const Chip = ({ active, onClick, label, family, icon }) => (
  <button
    type="button"
    className={`log-chip log-chip-${family || "default"}${active ? " is-active" : ""}`}
    onClick={onClick}
    title={active ? "Click to remove" : "Click to filter"}
  >
    {icon && <i className={`fa ${icon}`} aria-hidden />}
    <span>{label}</span>
  </button>
);

const ExpiryLog = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Navigate to /product-list with a query param that tells the Inventory
  // page to auto-open its Manage-Expiry modal for the given EPC. Used by
  // the per-row "Manage expiry" button below.
  const openManageExpiry = (epc) => {
    if (!epc) {
      showToast("This event has no product EPC attached", 2500, true, "warning");
      return;
    }
    navigate(`/product-list?expiryFor=${encodeURIComponent(epc)}`);
  };

  // Data
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [distincts, setDistincts] = useState({ users: [], roles: [] });
  const [counts, setCounts] = useState({ set: 0, cleared: 0, nearing: 0, expired: 0 });
  const [loading, setLoading] = useState(false);

  // Clear-log modal — password-gated (EXPIRY_CLEAR_PASSWORD env var).
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPassword, setClearPassword] = useState("");
  // Raw <input> rather than <TextField>, so it needs its own reveal toggle.
  const [showClearPassword, setShowClearPassword] = useState(false);
  const [clearing, setClearing] = useState(false);

  // The password itself is already cleared at each call site that closes the
  // modal; this just makes sure the reveal toggle can't persist so the field
  // reopens showing plain text.
  useEffect(() => {
    if (!clearOpen) setShowClearPassword(false);
  }, [clearOpen]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]); // subset of EXPIRY_EVENT_TYPES
  const [userFilter, setUserFilter] = useState("");
  const [tidFilter, setTidFilter] = useState("");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");

  // Sort
  const [sortBy, setSortBy] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");

  const totalPages = useMemo(
    () => (total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1),
    [total, pageSize]
  );

  // Debounce the free-text search.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const fetchEvents = useCallback(async (nextPage = page) => {
    setLoading(true);
    try {
      // Scope the backend query to only the expiry family. If the user has
      // ticked specific chips we narrow further; otherwise we ask for the
      // whole expiry whitelist.
      const effectiveTypes = selectedTypes.length > 0 ? selectedTypes : EXPIRY_EVENT_TYPES;
      const data = await api.getAuditEvents({
        page: nextPage,
        limit: pageSize,
        q: debouncedSearch,
        type: effectiveTypes,
        user: userFilter.trim(),
        tid: tidFilter.trim(),
        from: fromFilter,
        to: toFilter,
        sortBy,
        sortDir,
      });
      setEvents(data.events || []);
      setTotal(data.total || 0);
      setPage(data.page || nextPage);
    } catch (err) {
      showToast(err.message || "Failed to load expiry log", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, selectedTypes, userFilter, tidFilter,
      fromFilter, toFilter, sortBy, sortDir, showToast]);

  const fetchDistinctsAndCounts = useCallback(async () => {
    try {
      const [distinctRes, ...countRes] = await Promise.all([
        api.getAuditDistincts(),
        // One small query per bucket so the summary chips are accurate even
        // while filters are active. `limit=1` keeps the payload tiny.
        api.getAuditEvents({ type: ["TAG_ENDDATE_SET"],     limit: 1 }),
        api.getAuditEvents({ type: ["TAG_ENDDATE_CLEARED"], limit: 1 }),
        api.getAuditEvents({ type: ["TAG_NEARING_END"],     limit: 1 }),
        api.getAuditEvents({ type: ["TAG_EXPIRED"],         limit: 1 }),
      ]);
      setDistincts({
        users: distinctRes?.users || [],
        roles: distinctRes?.roles || [],
      });
      setCounts({
        set:     countRes[0]?.total || 0,
        cleared: countRes[1]?.total || 0,
        nearing: countRes[2]?.total || 0,
        expired: countRes[3]?.total || 0,
      });
    } catch {
      /* non-fatal */
    }
  }, []);

  // Reset to page 1 whenever filters change.
  useEffect(() => {
    setPage(1);
    fetchEvents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedTypes, userFilter, tidFilter, fromFilter, toFilter,
      sortBy, sortDir, pageSize]);

  useEffect(() => { fetchDistinctsAndCounts(); }, [fetchDistinctsAndCounts]);

  const toggleType = (type) =>
    setSelectedTypes((prev) => prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]);

  const clearFilters = () => {
    setSearch("");
    setSelectedTypes([]);
    setUserFilter("");
    setTidFilter("");
    setFromFilter("");
    setToFilter("");
    setSortBy("timestamp");
    setSortDir("desc");
  };

  const handleClearExpiry = async () => {
    if (!clearPassword) return;
    setClearing(true);
    try {
      const res = await api.clearExpiryLog(clearPassword);
      showToast(`Cleared ${res.cleared} expiry event(s)`, 2500, true, "success");
      setClearOpen(false);
      setClearPassword("");
      await Promise.all([fetchEvents(1), fetchDistinctsAndCounts()]);
    } catch (err) {
      if (err.status === 401) {
        showToast("Incorrect password", 3000, true, "danger");
      } else if (err.status === 503) {
        showToast("Set EXPIRY_CLEAR_PASSWORD in the backend .env first", 4000, true, "danger");
      } else {
        showToast(err.message || "Failed to clear expiry log", 3000, true, "danger");
      }
    } finally {
      setClearing(false);
    }
  };

  const activeFilterCount =
    (search.trim() ? 1 : 0) +
    selectedTypes.length +
    (userFilter.trim() ? 1 : 0) +
    (tidFilter.trim() ? 1 : 0) +
    (fromFilter ? 1 : 0) +
    (toFilter ? 1 : 0);

  const headerSort = (field, label) => {
    const active = sortBy === field;
    return (
      <th
        className={`log-th-sortable${active ? " is-active" : ""}`}
        onClick={() => {
          if (active) setSortDir(sortDir === "asc" ? "desc" : "asc");
          else { setSortBy(field); setSortDir("desc"); }
        }}
      >
        {label}
        <i className={`fa ${active ? (sortDir === "asc" ? "fa-sort-asc" : "fa-sort-desc") : "fa-sort"}`}
           style={{ marginLeft: 6, opacity: active ? 1 : 0.4 }} aria-hidden />
      </th>
    );
  };

  return (
    <div className="dashboard-container audit-log-page expiry-log-page">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">
              <i className="fa fa-calendar-times-o" style={{ marginRight: 10 }} aria-hidden />
              Expiry Log
            </h1>
            <p className="dashboard-subtitle">
              A dedicated timeline for tag end dates — set / cleared / nearing / expired. Separate from the main Log so it stays clean.
            </p>
          </div>
          <div className="log-actions">
            <button
              type="button"
              className="log-action-btn log-action-refresh"
              onClick={() => fetchEvents(page)}
              disabled={loading}
            >
              <i className={`fa ${loading ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              className="log-action-btn log-action-clear"
              onClick={() => { setClearOpen(true); setClearPassword(""); }}
              disabled={clearing}
              title="Wipe all expiry log events (password required)"
            >
              <i className="fa fa-trash" aria-hidden />
              <span>Clear Log</span>
            </button>
          </div>
        </div>

        {/* Summary strip — four colour-coded counters, clickable to filter. */}
        <div className="expiry-summary-grid">
          {[
            { key: "TAG_ENDDATE_SET",     label: "End dates set",    value: counts.set,     tone: "update" },
            { key: "TAG_ENDDATE_CLEARED", label: "End dates cleared",value: counts.cleared, tone: "auth-neutral" },
            { key: "TAG_NEARING_END",     label: "Nearing end",      value: counts.nearing, tone: "auth-bad" },
            { key: "TAG_EXPIRED",         label: "Expired",          value: counts.expired, tone: "delete-hard" },
          ].map((card) => {
            const active = selectedTypes.includes(card.key);
            return (
              <button
                key={card.key}
                type="button"
                className={`expiry-summary-card expiry-summary-${card.tone}${active ? " is-active" : ""}`}
                onClick={() => toggleType(card.key)}
              >
                <span className="expiry-summary-value">{card.value}</span>
                <span className="expiry-summary-label">{card.label}</span>
              </button>
            );
          })}
        </div>

        {/* Filter bar — same look as /audit-log, narrower field set. */}
        <div className="filters-section log-filters">
          <div className="log-search-row">
            <div className="log-search-wrap">
              <i className="fa fa-search log-search-icon" aria-hidden />
              <input
                type="text"
                className="log-search-input"
                placeholder="Search tag IDs, products, users, descriptions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button type="button" className="log-search-clear" onClick={() => setSearch("")}>
                  <i className="fa fa-times" aria-hidden />
                </button>
              )}
            </div>
            <div className="log-search-meta">
              {activeFilterCount > 0 && (
                <span className="log-active-filter-pill">
                  <i className="fa fa-filter" aria-hidden /> {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
                </span>
              )}
              <button type="button" className="log-mini-btn" onClick={clearFilters} disabled={activeFilterCount === 0}>
                <i className="fa fa-times" aria-hidden /> Clear filters
              </button>
            </div>
          </div>

          <div className="log-chip-row">
            <span className="log-chip-group-label">Event</span>
            <div className="log-chip-list">
              {EXPIRY_EVENT_TYPES.map((t) => {
                const meta = classifyType(t);
                return (
                  <Chip
                    key={t}
                    active={selectedTypes.includes(t)}
                    onClick={() => toggleType(t)}
                    label={meta.label}
                    family={meta.family}
                    icon={meta.icon}
                  />
                );
              })}
            </div>
          </div>

          <div className="log-input-row">
            <div className="log-input-field">
              <label className="log-input-label">User</label>
              <input
                className="log-input"
                type="text"
                list="expiry-user-list"
                placeholder="Filter by username…"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              />
              <datalist id="expiry-user-list">
                {distincts.users.map((u) => <option key={u} value={u} />)}
              </datalist>
            </div>
            <div className="log-input-field">
              <label className="log-input-label">Tag ID / EPC</label>
              <input
                className="log-input"
                type="text"
                placeholder="TID or EPC contains…"
                value={tidFilter}
                onChange={(e) => setTidFilter(e.target.value)}
              />
            </div>
            <div className="log-input-field">
              <label className="log-input-label">From</label>
              <input
                className="log-input"
                type="datetime-local"
                value={fromFilter}
                onChange={(e) => setFromFilter(e.target.value)}
              />
            </div>
            <div className="log-input-field">
              <label className="log-input-label">To</label>
              <input
                className="log-input"
                type="datetime-local"
                value={toFilter}
                onChange={(e) => setToFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="table-section">
          <div className="section-header log-results-header">
            <h2 className="section-title">Events</h2>
            <div className="log-results-meta">
              <span className="results-count">
                {loading ? "Loading…" : `Showing ${events.length} of ${total.toLocaleString()}`}
              </span>
              <select
                className="log-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value))}
                title="Page size"
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>
          </div>

          <div className="table-container">
            {events.length === 0 && !loading ? (
              <div className="empty-state">
                <i className="fa fa-calendar empty-state-icon" />
                <p>No expiry events yet. Set an end date on any tag to start populating this log.</p>
              </div>
            ) : (
              <table className="tags-table audit-log-table">
                <thead>
                  <tr>
                    {headerSort("timestamp", "Time")}
                    {headerSort("type", "Event")}
                    {headerSort("user", "User")}
                    <th>Tag · Product</th>
                    <th>Description</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((evt) => {
                    const id = evt._id || `${evt.type}-${evt.timestamp}-${evt.thingId}`;
                    const meta = classifyType(evt.type);
                    const ts = evt.timestamp || evt.createdAt;
                    const endDate = evt.details?.endDate;
                    const productName = evt.details?.productName || evt.details?.name || "";
                    const epc = evt.details?.epc || "";
                    const tid = evt.thingId || evt.details?.tid || "";
                    const linked = !!tid;
                    return (
                      <tr
                        key={id}
                        className={`log-row log-row-${meta.family}${linked ? " log-row-linked" : ""}`}
                        onClick={linked ? () => navigate(`/tags/${encodeURIComponent(tid)}`) : undefined}
                        title={linked ? "Open tag details" : undefined}
                      >
                        <td className="log-when" title={formatExact(ts)}>
                          <span className="log-when-rel">{formatWhen(ts)}</span>
                          <span className="log-when-abs">{formatExact(ts)}</span>
                        </td>
                        <td>
                          <span className={`log-type-badge log-type-${meta.family}`}>
                            <i className={`fa ${meta.icon}`} aria-hidden /> {meta.label}
                          </span>
                        </td>
                        <td>
                          <div className="log-user-cell">
                            <span className="log-user-name">{evt.user || "—"}</span>
                            {evt.role && <span className={`log-role-pill log-role-${evt.role}`}>{evt.role}</span>}
                          </div>
                        </td>
                        <td>
                          {productName ? (
                            <div className="expiry-product-name" title={productName}>
                              <i className="fa fa-cubes" aria-hidden /> {productName}
                            </div>
                          ) : (
                            <div className="expiry-product-missing">
                              <i className="fa fa-question-circle" aria-hidden /> Unknown product
                            </div>
                          )}
                          <span className="expiry-tag-code">
                            <i className="fa fa-tag" aria-hidden /> {tid || "—"}
                          </span>
                          {endDate && (
                            <div className="expiry-tag-date">
                              <i className="fa fa-calendar-o" aria-hidden /> {new Date(endDate).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="log-desc">{evt.description || "—"}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="expiry-manage-btn"
                            onClick={() => openManageExpiry(epc)}
                            disabled={!epc}
                            title={epc ? `Open Inventory and edit expiry for ${productName || epc}` : "No EPC on this event"}
                          >
                            <i className="fa fa-calendar" aria-hidden /> Manage expiry
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="audit-pagination">
            <button type="button" className="log-mini-btn"
              disabled={page <= 1 || loading}
              onClick={() => fetchEvents(page - 1)}>
              <i className="fa fa-chevron-left" aria-hidden /> Prev
            </button>
            <span className="audit-page-info">Page <strong>{page}</strong> of {totalPages}</span>
            <button type="button" className="log-mini-btn"
              disabled={page >= totalPages || loading}
              onClick={() => fetchEvents(page + 1)}>
              Next <i className="fa fa-chevron-right" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {/* ── Clear Expiry Log modal ─────────────────────────────────────── */}
      {clearOpen && (
        <div
          className="modal-overlay"
          onClick={() => { if (!clearing) { setClearOpen(false); setClearPassword(""); } }}
        >
          <div className="modal-container tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ backgroundColor: "var(--error-subtle, #FEE2E2)", color: "var(--error, #DC2626)" }}>
                <i className="fa fa-trash" />
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Clear Expiry Log</span>
                <span className="modal-subtitle">
                  This permanently deletes all end-date set, cleared, nearing-end and expired events.
                  This action cannot be undone.
                </span>
              </div>
              <div
                className="modal-close-btn"
                onClick={() => { if (!clearing) { setClearOpen(false); setClearPassword(""); } }}
              >
                <i className="fa fa-times" />
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <label className="form-label" htmlFor="expiry-clear-password">
                    Confirmation password
                  </label>
                </div>
                <div className="log-input-reveal">
                  <input
                    id="expiry-clear-password"
                    type={showClearPassword ? "text" : "password"}
                    className="log-input"
                    placeholder="Enter the expiry-clear password"
                    value={clearPassword}
                    onChange={(e) => setClearPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && clearPassword && !clearing) handleClearExpiry();
                    }}
                    autoFocus
                    autoComplete="new-password"
                    disabled={clearing}
                  />
                  <button
                    type="button"
                    className="log-input-reveal-btn"
                    onClick={() => setShowClearPassword((v) => !v)}
                    disabled={clearing}
                    aria-label={showClearPassword ? "Hide password" : "Show password"}
                    aria-pressed={showClearPassword}
                    title={showClearPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fa ${showClearPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => { if (!clearing) { setClearOpen(false); setClearPassword(""); } }}
              >
                <i className="fa fa-times" />
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn"
                style={{
                  backgroundColor: clearPassword && !clearing ? "var(--error, #DC2626)" : undefined,
                  color: clearPassword && !clearing ? "#fff" : undefined,
                  opacity: !clearPassword || clearing ? 0.5 : 1,
                  cursor: !clearPassword || clearing ? "not-allowed" : "pointer",
                }}
                onClick={!clearPassword || clearing ? undefined : handleClearExpiry}
                title={clearPassword ? "Clear all expiry events" : "Enter the password first"}
              >
                <i className={`fa ${clearing ? "fa-spinner fa-spin" : "fa-trash"}`} />
                <span>{clearing ? "Clearing…" : "Yes, clear all"}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiryLog;
